/**
 * statusTransitions.js — Valid job status transition DAG
 *
 * Defines which status moves are allowed from each state.
 * Forward jumps are allowed — any earlier status can skip ahead
 * to any later status in the pipeline flow.
 *
 * Flow order: intake → quoted → confirmed → shooting → post_production → partial_delivery → delivered → invoiced → paid → archived
 * Any active status can also → void
 * void → intake (re-open)
 */

export const FLOW = ['intake', 'quoted', 'confirmed', 'shooting', 'post_production', 'partial_delivery', 'delivered', 'invoiced', 'paid', 'archived'];

export const ALL_STATUSES = [...FLOW, 'void'];

export const VALID_TRANSITIONS = Object.fromEntries(
  FLOW.map((status, idx) => {
    const forward = FLOW.slice(idx + 1);
    // All active (non-terminal) statuses can void
    if (!['paid', 'archived', 'void'].includes(status)) {
      return [status, [...forward, 'void']];
    }
    if (status === 'paid') return [status, ['archived']];
    return [status, forward];
  }).concat([['void', ['intake']]])
);

/**
 * Get valid next statuses for a given current status.
 * @param {string} currentStatus
 * @returns {string[]} array of valid next statuses
 */
export function getValidTransitions(currentStatus) {
  return VALID_TRANSITIONS[currentStatus] || [];
}

/**
 * Check whether a specific transition is allowed.
 * @param {string} from - current status
 * @param {string} to - desired next status
 * @returns {boolean}
 */
export function isValidTransition(from, to) {
  const allowed = VALID_TRANSITIONS[from];
  return allowed ? allowed.includes(to) : false;
}

/**
 * Label map for action buttons — describes what the transition means.
 */
export const TRANSITION_LABELS = {
  intake:           'New Intake',
  quoted:           'Mark Quoted',
  confirmed:        'Confirm Booking',
  shooting:         'Start Shooting',
  post_production:  'Post Production',
  partial_delivery: 'Partial Delivery',
  delivered:        'Mark Delivered',
  invoiced:         'Mark Invoiced',
  paid:             'Mark Paid',
  void:             'Void Job',
  archived:         'Archive',
};

/**
 * Button styles for each transition target status.
 */
export const TRANSITION_STYLES = {
  intake:           'bg-[#A47B1A]/10 border-[#A47B1A]/30 text-[#A47B1A]',
  quoted:           'bg-[#1a7fa3]/10 border-[#1a7fa3]/30 text-[#1a7fa3]',
  confirmed:        'bg-[#7049a8]/10 border-[#7049a8]/30 text-[#7049a8]',
  shooting:         'bg-[#2E7D9B]/10 border-[#2E7D9B]/30 text-[#2E7D9B]',
  post_production:  'bg-[#5B5FC7]/10 border-[#5B5FC7]/30 text-[#5B5FC7]',
  partial_delivery: 'bg-[#C67A1E]/10 border-[#C67A1E]/30 text-[#C67A1E]',
  delivered:        'bg-[#1e8b4a]/10 border-[#1e8b4a]/30 text-[#1e8b4a]',
  invoiced:         'bg-[#c43d28]/10 border-[#c43d28]/30 text-[#c43d28]',
  paid:             'bg-[#1e8b4a]/10 border-[#1e8b4a]/30 text-[#1e8b4a]',
  void:             'bg-red-accent/10 border-red-accent/30 text-red-accent',
  archived:         'bg-surface-2 border-border text-text-dim',
};
